import { Button, InputNumber, Segmented, Space, Tooltip, Typography, Alert, Tag, Table, Flex, Popconfirm } from 'antd';
import {
    ArrowUpOutlined, ArrowDownOutlined, DeleteOutlined, PlusOutlined, PlayCircleOutlined, StopOutlined,
    TeamOutlined, AppstoreOutlined, PauseCircleOutlined, FieldTimeOutlined, StepForwardOutlined, VideoCameraOutlined,
} from '@ant-design/icons';
import FlowTimeline, { stepLabels } from './FlowTimeline';

const { Text } = Typography;

// Minutes the admin can add to the live step with one click.
const EXTEND_OPTIONS = [1, 5, 10];

/**
 * Admin: the flow — an ordered list of timed steps, each everyone in the
 * common room or a breakout round. Every breakout round is planned from the
 * same groups and ideal room (and gets its own arrangement in Rooms); the
 * Steps view above the table shows the whole run, and runs live once started.
 *
 * Editing is locked while the flow runs — moving steps would move people
 * mid-conversation. What a running flow can do instead: pause (countdowns
 * freeze for everyone), move on to the next step now, and extend the live
 * step by a few minutes — for this run only; the planned lengths are kept.
 */
function FlowEditor({
    steps, onChange, position, startedAt, running, paused, canStart, startHint,
    onStart, onStop, onPause, onResume, onExtend, onNext, commonRoomName, onJoinCommonRoom,
}) {
    const labels = stepLabels(steps);
    const update = (i, patch) => onChange(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));
    const move = (i, by) => {
        const next = [...steps];
        [next[i], next[i + by]] = [next[i + by], next[i]];
        onChange(next);
    };
    const add = kind => onChange([...steps, { key: `new-${Date.now().toString(36)}`, kind, duration_minutes: kind === 'common' ? 10 : 20, seed: 0 }]);
    const live = position && !position.finished ? position.index : -1;
    const liveIsCommon = live >= 0 && steps[live]?.kind === 'common';

    const columns = [
        {
            key: 'n',
            width: 44,
            render: (_, s, i) => <Tag color={i === live ? 'processing' : 'default'} style={{ margin: 0 }}>{i + 1}</Tag>,
        },
        {
            key: 'kind',
            title: 'Step',
            render: (_, s, i) => (
                <Space wrap size={8}>
                    <Segmented
                        size="small"
                        value={s.kind}
                        disabled={running}
                        onChange={kind => update(i, { kind })}
                        options={[
                            { label: 'Common room', value: 'common', icon: <TeamOutlined /> },
                            { label: 'Breakout', value: 'breakout', icon: <AppstoreOutlined /> },
                        ]}
                    />
                    {s.kind === 'breakout' && <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>{labels[i]}</Text>}
                </Space>
            ),
        },
        {
            key: 'minutes',
            title: 'Length',
            width: 170,
            render: (_, s, i) => (
                <Space size={6}>
                    <InputNumber
                        size="small"
                        min={1}
                        max={600}
                        value={s.duration_minutes}
                        disabled={running}
                        onChange={v => update(i, { duration_minutes: v || 1 })}
                        suffix="min"
                        style={{ width: 96 }}
                    />
                    {s.extended_minutes > 0 && (
                        <Tooltip title="Added during this run — the planned length is unchanged">
                            <Tag color="success" style={{ margin: 0 }}>+{s.extended_minutes}′</Tag>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        !running && {
            key: 'actions',
            width: 110,
            align: 'right',
            render: (_, s, i) => (
                <Space size={0}>
                    <Button type="text" size="small" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up" />
                    <Button type="text" size="small" icon={<ArrowDownOutlined />} disabled={i === steps.length - 1} onClick={() => move(i, 1)} aria-label="Move down" />
                    <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={steps.length === 1}
                        onClick={() => onChange(steps.filter((_, j) => j !== i))}
                        aria-label="Delete step"
                    />
                </Space>
            ),
        },
    ].filter(Boolean);

    return (
        <Flex vertical gap={16}>
            <FlowTimeline steps={steps} position={position} startedAt={startedAt} paused={paused} />

            <Flex wrap align="center" gap={12}>
                {running ? (
                    <>
                        {paused ? (
                            <Button type="primary" icon={<PlayCircleOutlined />} onClick={onResume}>Resume</Button>
                        ) : (
                            <Button icon={<PauseCircleOutlined />} onClick={onPause} disabled={position?.finished}>Pause</Button>
                        )}
                        {live >= 0 && (
                            <Popconfirm
                                title={live === steps.length - 1 ? 'End the flow now?' : `End ${labels[live]} and move to ${labels[live + 1]}?`}
                                description="Everyone moves on straight away."
                                okText={live === steps.length - 1 ? 'End flow' : 'Next step'}
                                onConfirm={onNext}
                            >
                                <Button icon={<StepForwardOutlined />}>
                                    {live === steps.length - 1 ? 'End flow' : `Next: ${labels[live + 1]}`}
                                </Button>
                            </Popconfirm>
                        )}
                        {live >= 0 && (
                            <Space size={6}>
                                <FieldTimeOutlined style={{ color: 'var(--text-tertiary)' }} />
                                <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>Extend {labels[live]}</Text>
                                <Space.Compact>
                                    {EXTEND_OPTIONS.map(m => (
                                        <Button key={m} onClick={() => onExtend(m)}>+{m}′</Button>
                                    ))}
                                </Space.Compact>
                            </Space>
                        )}
                        {liveIsCommon && commonRoomName && (
                            <Button type="primary" icon={<VideoCameraOutlined />} onClick={onJoinCommonRoom}>
                                Join common room
                            </Button>
                        )}
                        <Button danger icon={<StopOutlined />} onClick={onStop}>Stop</Button>
                    </>
                ) : (
                    <Tooltip title={startHint}>
                        <Button type="primary" icon={<PlayCircleOutlined />} onClick={onStart} disabled={!canStart}>
                            Start flow
                        </Button>
                    </Tooltip>
                )}
            </Flex>

            {running && (
                <Alert
                    type={position?.finished ? 'success' : paused ? 'warning' : 'info'}
                    showIcon
                    title={position?.finished
                        ? 'The flow has finished.'
                        : paused ? 'Paused — everyone’s countdown is frozen.' : 'Running — everyone’s view follows along.'}
                    description="Stop the flow to change its steps."
                />
            )}

            <Table
                size="small"
                rowKey="key"
                columns={columns}
                dataSource={steps}
                pagination={false}
                rowClassName={(_, i) => (i === live ? 'gathering-flow-row-live' : '')}
            />
            {!running && (
                <Space wrap>
                    <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => add('common')}>Common room</Button>
                    <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => add('breakout')}>Breakout round</Button>
                </Space>
            )}
        </Flex>
    );
}

export default FlowEditor;
